import { useGroupLifeQuote } from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { groupLifeService } from '@/services';
import { useQuery } from '@tanstack/react-query';
import { useQuotationVersionId } from './use-quotation-version-id.hook';

/**
 * Indica, por `stepCode` do schema, se o step da cotação PME já tem dados —
 * seja no contexto in-memory da sessão, seja no snapshot hidratado do
 * InsureMO. Usado pelo stepper para liberar navegação direta pelo número do
 * step; steps vazios continuam alcançáveis apenas pelo fluxo sequencial.
 *
 * Compartilha a queryKey do `useHydrateGroupLifeQuote` (mesmo endpoint) e
 * apenas observa o cache (`refetchOnMount: false`) — não dispara fetch extra.
 */
export function useGroupLifeStepFilled(): (stepCode: string) => boolean {
  const { policyId } = useQuoteParams();
  const versionId = useQuotationVersionId();
  const {
    stipulator,
    plan,
    commercialization,
    calculation,
    stipulatorMembers,
    subStipulators,
  } = useGroupLifeQuote();

  const { data: snapshot } = useQuery({
    queryKey: versionId
      ? ['group-life', 'quote-at-version', policyId, versionId]
      : ['group-life', 'quote-details', policyId],
    queryFn: async () => {
      if (!policyId) throw new Error('policyId é obrigatório');
      const result = versionId
        ? await groupLifeService.getQuoteAtVersion(policyId, versionId)
        : await groupLifeService.getQuoteDetails(policyId);
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Falha ao carregar a cotação Group Life'
        );
      }
      return result.data;
    },
    enabled: !!policyId,
    select: d => ({
      hasStipulator: Boolean(d.stipulator),
      hasPlan: Boolean(d.plan),
      hasMembers: (d.stipulatorMembers?.length ?? 0) > 0,
      hasCommercialization: Boolean(d.commercialization),
      hasCalculation: Boolean(d.calculation),
    }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const hasSessionMembers =
    stipulatorMembers.length > 0 ||
    subStipulators.some(sub => sub.members.length > 0);

  return (stepCode: string): boolean => {
    switch (stepCode) {
      case 'CLIENT_INFO':
        return Boolean(stipulator) || Boolean(snapshot?.hasStipulator);
      case 'PLAN':
        return Boolean(plan) || Boolean(snapshot?.hasPlan);
      case 'MEMBERS':
        // As vidas podem não vir no snapshot de detalhes (o grid do step é
        // paginado no server) e o contexto é limpo na hidratação. Cálculo
        // existente implica vidas — o prêmio é tarifado vida a vida.
        return (
          hasSessionMembers ||
          Boolean(snapshot?.hasMembers) ||
          Boolean(calculation) ||
          Boolean(snapshot?.hasCalculation)
        );
      case 'COMMERCIALIZATION':
        return (
          Boolean(commercialization) || Boolean(snapshot?.hasCommercialization)
        );
      case 'CALCULATION':
        return Boolean(calculation) || Boolean(snapshot?.hasCalculation);
      default:
        return false;
    }
  };
}
